import React, { useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardUrl } from '../../utils/dashboardUrl';
import loginImage from '../../assets/images/services-1.png';
import tecnvironsLogo from '../../assets/images/Tecnvirons-logo.jpeg';
import 'react-phone-input-2/lib/style.css';
import '../common/phone-input-overrides.css';
import './LoginModal.css'; // Reusing the same CSS

const AdminLoginModal = ({ isOpen, onClose, onSwitchToSignup, onForgotPassword }) => {
  const [authType, setAuthType] = useState('email');
  const { adminLogin, handleAdminLoginSuccess } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const toE164 = (value) => (value ? `+${value}` : '');

  const isValidInternationalPhone = (value) => {
    if (!value) return false;
    const parsed = parsePhoneNumberFromString(toE164(value));
    return !!parsed && parsed.isValid();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (authType === 'email') {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else if (authType === 'phone') {
      if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!isValidInternationalPhone(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const loginData = authType === 'email'
        ? { email: formData.email, password: formData.password }
        : { phone: toE164(formData.phone), password: formData.password };

      const loginIdentifier = loginData.email || loginData.phone;
      const result = await adminLogin(loginIdentifier, formData.password, authType);

      if (result.success) {
        onClose();
        handleAdminLoginSuccess(result.user);
        let redirect = 'https://app.tecnvi-ai.com/admin-users/overview';
        try {
          redirect = getDashboardUrl('/admin-users/overview');
        } catch (err) {
          // fall back to hard-coded URL above
        }
        window.location.href = redirect;
      } else {
        setErrors({ general: result.error || 'Invalid email/phone or password.' });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div
        className="login-modal-container auth-modal"
        onClick={e => e.stopPropagation()}
        style={{ '--modal-bg-image': `url(${loginImage})` }}
      >
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
        <div className="modal-form-section">
          <div className="form-content">
            <div className="auth-modal-header text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginTop: '2px' }}>
              <div className="modal-logo" style={{ marginBottom: '20px', width: '100%', display: 'flex', justifyContent: 'center' }}>
                <img
                  src={tecnvironsLogo}
                  alt="Tecnvirons"
                  className="modal-logo-image"
                  style={{ width: '60px', maxWidth: '100%', height: 'auto', objectFit: 'contain', backgroundColor: 'transparent', display: 'block', margin: '0 auto' }}
                />
              </div>
            </div>

            <div className="auth-toggle">
              <button className="toggle-btn" onClick={onSwitchToSignup}>HQ Sign Up</button>
              <button className="toggle-btn active">HQ Log In</button>
            </div>

            <div className="form-main">
              <h2 className="form-title text-center">Welcome Back, HQ</h2>
              <p className="form-subtitle text-center">Sign in to the Headquarters Dashboard</p>

              {errors.general && (
                <div className="error-message">
                  {errors.general}
                </div>
              )}

              <form onSubmit={handleSubmit} className="login-form">
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthType('email');
                      setFormData(prev => ({ ...prev, phone: '' }));
                      setErrors({});
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: authType === 'email' ? '#6366f1' : '#e8e8e8',
                      color: authType === 'email' ? 'white' : '#999',
                      cursor: 'pointer',
                      fontWeight: authType === 'email' ? '600' : '500',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.3s ease',
                      boxShadow: authType === 'email' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthType('phone');
                      setFormData(prev => ({ ...prev, email: '' }));
                      setErrors({});
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: authType === 'phone' ? '#6366f1' : '#e8e8e8',
                      color: authType === 'phone' ? 'white' : '#999',
                      cursor: 'pointer',
                      fontWeight: authType === 'phone' ? '600' : '500',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.3s ease',
                      boxShadow: authType === 'phone' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    Phone
                  </button>
                </div>

                {authType === 'email' && (
                  <div className="modal-form-group">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="email">Email</label>
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                )}

                {authType === 'phone' && (
                  <div className="modal-form-group modal-form-group-phone">
                    <label htmlFor="phone" style={{ display: 'block', marginBottom: '8px', color: '#4b5563', fontSize: '14px' }}>Phone Number</label>
                    <PhoneInput
                      value={formData.phone}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, phone: value }));
                        if (errors.phone) {
                          setErrors(prev => ({ ...prev, phone: '' }));
                        }
                      }}
                      country="in"
                      countryCodeEditable={true}
                      disableDropdown={false}
                      enableSearch={true}
                      preferredCountries={['in', 'us', 'gb', 'ae', 'sa', 'pk', 'bd']}
                      searchPlaceholder="Search country..."
                      searchNotFound="No country found"
                      disabled={isLoading}
                    />
                    {errors.phone && <span className="field-error">{errors.phone}</span>}
                  </div>
                )}

                <div className="modal-form-group">
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder=" "
                      value={formData.password}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="password">Password</label>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="field-error">{errors.password}</span>}
                </div>

                <div className="form-options">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="rememberMe"
                      name="rememberMe"
                      className="auth-custom-checkbox"
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    <label htmlFor="rememberMe">Remember me</label>
                  </div>
                  <button
                    type="button"
                    className="forgot-password-btn"
                    onClick={() => onForgotPassword && onForgotPassword()}
                    disabled={isLoading}
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </button>
              </form>

              <div className="form-footer">
                <p><span>Don't have a HQ account?</span> <button type="button" onClick={onSwitchToSignup} className="link-btn">Sign Up</button></p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-image-section">
          <img src={loginImage} alt="Tecnvirons HQ Login" className="modal-image" />
          <div className="image-overlay">
            <div className="decorative-shape top-left-shape"></div>
            <div className="decorative-shape bottom-right-shape"></div>
            <div className="overlay-content">
              <div className="overlay-card">
                <h3>Headquarters Dashboard</h3>
                <p>Access powerful tools to manage your platform and oversee client activities.</p>
              </div>
              <div className="bottom-overlay">
                <h3>Control at Your Fingertips</h3>
                <p>Resume your platform management duties.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginModal;
